import { useState } from "react";
import axios from "axios";
import { Reveal } from "../anim";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getCurrency, fmtMoney } from "../../lib/currency";
import { useSite, t } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TIERS = [
  {
    id: "starter",
    name: "Starter",
    sub: "For first-time backers",
    accent: "#A7D3E8",
    prices: { usd: { price: 19, orig: 29 }, php: { price: 1500, orig: 3000 } },
    features: [
      "Early access to the campaign drops, and new developments",
      "Digital catalogues of new products, and designer tips",
      "Backer-only exclusive discounts",
      "Choose your Welcome freebie: Personalized gift or a Day Pass at the Studio",
    ],
  },
  {
    id: "collaborator",
    name: "Collaborator",
    sub: "For brands and manufacturers ready to scale",
    tag: "Most popular",
    featured: true,
    accent: "#D7E85C",
    prices: { usd: { price: 69, orig: 99 }, php: { price: 15000, orig: 30000 } },
    features: [
      "Everything in Starter",
      "3-piece product collection per month",
      "Pre-production crowdfunding for your signature collection",
      "Flexible add-on services",
      "5% off all future orders",
    ],
  },
  {
    id: "vip_buyer",
    name: "VIP Buyer",
    sub: "For serious partners",
    accent: "#FF8473",
    prices: { usd: { price: 199, orig: 299 }, php: { price: 99000, orig: 120000 } },
    features: [
      "Everything in Collaborator",
      "Free packaging design for signature collections.",
      "1:1 sourcing consultation call",
      "End-to-end product development, prototype checking, sourcing, and logistics management.",
      "Lifetime early-access status",
    ],
  },
];

const DURATIONS = [
  { m: 1, label: "1 Month" },
  { m: 3, label: "3 Months" },
  { m: 6, label: "6 Months" },
];

// PHP total-bill discounts by duration (per tier group)
const DISCOUNT_PHP = { starter: { 3: 500, 6: 1000 }, collaborator: { 3: 1000, 6: 3000 }, vip_buyer: { 3: 2000, 6: 5000 } };
const DISCOUNT_USD = { starter: { 3: 9, 6: 18 }, collaborator: { 3: 18, 6: 54 }, vip_buyer: { 3: 36, 6: 90 } };
const totalFor = (tierId, monthly, months, code) => {
  const disc = (code === "php" ? DISCOUNT_PHP : DISCOUNT_USD)[tierId]?.[months] || 0;
  return Math.max(0, monthly * months - disc);
};
const discFor = (tierId, months, code) => (code === "php" ? DISCOUNT_PHP : DISCOUNT_USD)[tierId]?.[months] || 0;

export const CreateWithUs = () => {
  const [loading, setLoading] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [months, setMonths] = useState(3);
  const activeId = hovered ?? "collaborator";
  const { code, symbol } = getCurrency();
  const { site } = useSite();
  const cmsTiers = t(site, "services.tiers", null);
  const TIERS = Array.isArray(cmsTiers) && cmsTiers.length > 0 ? cmsTiers : DEFAULT_TIERS;

  const back = async (id) => {
    setLoading(id);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        package_id: id,
        origin_url: window.location.origin,
        months,
        currency: code,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <section id="join" data-testid="join-section" className="relative">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-24 md:py-40">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
          <Reveal className="lg:col-span-8">
            <p className="text-lg md:text-2xl text-[#FFFFFF] leading-snug max-w-2xl">
              Idea to launch, thoughtfully designed.
              <br />
              <span className="text-[#EBE6E0]">Built through global collaboration.</span>
            </p>
            <h2 className="mt-8 font-display font-black uppercase tracking-tighter leading-[0.82] text-[16vw] lg:text-[9vw] text-[#EBE6E0]">
              Create<br /><span className="text-[#FF8473]">with us.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4 text-right">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-[#D7E85C]">
              Choose your services
            </p>
            <p className="mt-4 text-[#FFFFFF]">
              Slots are filling up! Reserve now.
              <br />
              Let's get your project started!
            </p>
            <button
              data-testid="browse-all-services-btn"
              onClick={() => document.getElementById("join")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-5 inline-flex items-center bg-[#D7E85C] text-[#1F3864] px-6 py-3 rounded-full font-bold uppercase text-sm hover:bg-[#FFFFFF] active:scale-95 transition-[background,transform,color] duration-200"
            >
              Browse all services
            </button>
          </Reveal>
        </div>

        {/* duration selector */}
        <Reveal className="mb-8 md:mb-12">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#FFFFFF]/70 mr-1">Choose duration</span>
            <div className="flex items-center gap-2">
              {DURATIONS.map((d) => (
                <button
                  key={d.m}
                  type="button"
                  data-testid={`duration-${d.m}`}
                  onClick={() => setMonths(d.m)}
                  className={`px-4 md:px-5 py-2 rounded-full text-xs md:text-sm font-bold uppercase tracking-wide border whitespace-nowrap transition-[background,border-color,color] duration-200 ${
                    months === d.m
                      ? "bg-[#D7E85C] border-[#D7E85C] text-[#1F3864]"
                      : "border-white/25 text-[#EBE6E0] hover:border-[#D7E85C]"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {TIERS.map((t, i) => {
            const active = activeId === t.id;
            const pr = t.prices[code];
            const btnStyle =
              t.id === "collaborator"
                ? active
                  ? "bg-[#D7E85C] text-[#1F3864] hover:bg-[#EBE6E0]"
                  : "bg-[#EBE6E0] text-[#1F3864] hover:bg-[#D7E85C]"
                : "bg-[#EBE6E0] text-[#1F3864] hover:bg-[#D7E85C]";
            return (
              <Reveal key={t.id} delay={i * 0.08}>
                <div
                  data-testid={`tier-card-${t.id}`}
                  onMouseEnter={() => setHovered(t.id)}
                  onMouseLeave={() => setHovered(null)}
                  className={`group relative h-full flex flex-col rounded-2xl border p-7 md:p-8 transition-[transform,border-color] duration-300 hover:-translate-y-2 ${
                    active ? "bg-[#16294B] border-[#D7E85C]" : "bg-[#13233E] border-white/10"
                  }`}
                >
                  {t.featured && (
                    <span className="absolute -top-3 left-7 bg-[#D7E85C] text-[#1F3864] text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                      {t.tag}
                    </span>
                  )}
                  <span className="text-sm font-bold uppercase tracking-[0.2em]" style={{ color: t.accent }}>
                    {t.name}
                  </span>
                  <span className="mt-1 text-xs text-[#FFFFFF]">{t.sub}</span>
                  <div className="mt-6 flex items-baseline gap-2 flex-wrap">
                    <span className="text-2xl text-[#FFFFFF]/50 line-through" data-testid={`tier-orig-${t.id}`}>{fmtMoney(symbol, pr.orig)}</span>
                    <span className="font-display font-black text-5xl text-[#EBE6E0]" data-testid={`tier-price-${t.id}`}>{fmtMoney(symbol, pr.price)}</span>
                    <span className="text-[#FFFFFF] text-sm">/month</span>
                  </div>
                  <span className="mt-1 inline-block text-xs font-bold text-[#D7E85C]" data-testid={`tier-total-${t.id}`}>
                    Limited-time offer · billed {fmtMoney(symbol, totalFor(t.id, pr.price, months, code))} for {months} mo
                    {discFor(t.id, months, code) > 0 && (
                      <span className="text-[#FFFFFF]/70"> (save {fmtMoney(symbol, discFor(t.id, months, code))})</span>
                    )}
                  </span>

                  <ul className="mt-8 flex-1 flex flex-col gap-3">
                    {t.features.map((f, k) => (
                      <li key={k} className="flex items-start gap-3 text-[#EBE6E0]/90 text-sm md:text-base">
                        <Check size={18} className="mt-0.5 shrink-0" style={{ color: t.accent }} />
                        {f.replace(/\.\s*$/, "")}
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`tier-back-${t.id}`}
                    disabled={loading !== null}
                    onClick={() => back(t.id)}
                    className={`mt-8 inline-flex items-center justify-center gap-2 py-3.5 rounded-full font-bold uppercase text-sm tracking-wide active:scale-95 transition-[background,transform,color] duration-200 disabled:opacity-60 ${btnStyle}`}
                  >
                    {loading === t.id ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Redirecting…
                      </>
                    ) : (
                      `Go ${t.name}`
                    )}
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
